/* Defy The Norm / section behaviour. Loaded as a module after Emerge. */

/* ---------- Tabs ---------- */
class DtnTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.select(next, true);
        }
      });
    });
  }
  select(index, focus) {
    this.tabs.forEach((tab, i) => {
      const on = i === index;
      tab.setAttribute('aria-selected', on);
      tab.tabIndex = on ? 0 : -1;
      const panel = this.querySelector('#' + tab.getAttribute('aria-controls'));
      if (panel) panel.hidden = !on;
      if (on && focus) tab.focus();
    });
  }
}
if (!customElements.get('dtn-tabs')) customElements.define('dtn-tabs', DtnTabs);

/* ---------- Hero fit ----------
   CSS sizes the hero to the screen under the header and scales the headline.
   This only steps the headline down if a long custom heading would still
   push the buttons below the fold. */
function dtnFitHeroes() {
  document.querySelectorAll('.dtn-hero[data-fit="true"]').forEach((hero) => {
    const h1 = hero.querySelector('.dtn-h1');
    const grid = hero.querySelector('.dtn-hero__grid');
    if (!h1 || !grid) return;
    h1.style.fontSize = '';
    const target = parseFloat(getComputedStyle(grid).minHeight);
    if (!target) return;
    let size = parseFloat(getComputedStyle(h1).fontSize);
    let guard = 0;
    while (grid.offsetHeight > target + 1 && size > 40 && guard++ < 40) {
      size -= 3;
      h1.style.fontSize = size + 'px';
    }
  });
}
let dtnFitTimer;
const dtnFitSoon = () => { clearTimeout(dtnFitTimer); dtnFitTimer = setTimeout(dtnFitHeroes, 120); };
dtnFitHeroes();
if (document.fonts && document.fonts.ready) document.fonts.ready.then(dtnFitHeroes);
let dtnLastW = window.innerWidth;
window.addEventListener('resize', () => {
  // Ignore mobile address-bar height changes so the hero never jumps while scrolling
  if (window.innerWidth === dtnLastW) return;
  dtnLastW = window.innerWidth;
  dtnFitSoon();
});
document.addEventListener('shopify:section:load', dtnFitSoon);

/* ---------- Countdown ---------- */
class DtnCountdown extends HTMLElement {
  connectedCallback() {
    const raw = (this.dataset.end || '').trim().replace(' ', 'T');
    this.end = new Date(raw).getTime();
    if (Number.isNaN(this.end)) { this.hidden = true; return; }
    this.els = { d: this.querySelector('[data-d]'), h: this.querySelector('[data-h]'), m: this.querySelector('[data-m]'), s: this.querySelector('[data-s]') };
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }
  disconnectedCallback() { clearInterval(this.timer); }
  tick() {
    const left = Math.max(0, this.end - Date.now());
    if (left === 0) {
      clearInterval(this.timer);
      const msg = this.dataset.expired;
      if (msg) { this.innerHTML = ''; const p = document.createElement('p'); p.className = 'dtn-label dtn-accent'; p.textContent = msg; this.appendChild(p); }
      else this.hidden = true;
      return;
    }
    const pad = (n) => String(n).padStart(2, '0');
    this.els.d.textContent = pad(Math.floor(left / 864e5));
    this.els.h.textContent = pad(Math.floor((left % 864e5) / 36e5));
    this.els.m.textContent = pad(Math.floor((left % 36e5) / 6e4));
    this.els.s.textContent = pad(Math.floor((left % 6e4) / 1e3));
  }
}
if (!customElements.get('dtn-countdown')) customElements.define('dtn-countdown', DtnCountdown);

/* ---------- Kit builder ---------- */
class DtnKit extends HTMLElement {
  connectedCallback() {
    this.threshold = parseInt(this.dataset.threshold || '0', 10);
    const lang = document.documentElement.lang || 'en-US';
    try {
      this.fmt = new Intl.NumberFormat(lang, { style: 'currency', currency: this.dataset.currency || 'USD' });
    } catch (e) {
      this.fmt = { format: (n) => '$' + n.toFixed(2) };
    }
    this.steps = [...this.querySelectorAll('[data-step]')].map((el) => {
      let products = [];
      try { products = JSON.parse(el.querySelector('[data-kit-products]').textContent); } catch (e) {}
      const step = { el, mode: el.dataset.mode, products, picked: new Map() };
      el.querySelectorAll('.dtn-opt').forEach((btn) => {
        btn.addEventListener('click', () => this.toggle(step, btn));
        if (btn.getAttribute('aria-pressed') === 'true') this.pick(step, btn.dataset.product);
      });
      return step;
    });
    this.lines = this.querySelector('[data-lines]');
    this.totalEl = this.querySelector('[data-total]');
    this.bar = this.querySelector('[data-bar]');
    this.ship = this.querySelector('[data-ship]');
    this.error = this.querySelector('[data-error]');
    this.addBtn = this.querySelector('[data-add]');
    this.addLabel = this.addBtn.textContent;
    this.addBtn.addEventListener('click', () => this.add());
    this.render();
  }

  product(step, id) { return step.products.find((p) => String(p.id) === String(id)); }

  pick(step, id) {
    if (id === 'none') return;
    const p = this.product(step, id);
    if (!p) return;
    const variant = p.variants.find((v) => v.id === p.selected && v.available) || p.variants.find((v) => v.available);
    if (variant) step.picked.set(String(p.id), variant.id);
  }

  toggle(step, btn) {
    const id = btn.dataset.product;
    if (step.mode === 'single') {
      step.picked.clear();
      step.el.querySelectorAll('.dtn-opt').forEach((b) => b.setAttribute('aria-pressed', b === btn));
      this.pick(step, id);
    } else {
      const on = btn.getAttribute('aria-pressed') !== 'true';
      btn.setAttribute('aria-pressed', on);
      if (on) this.pick(step, id); else step.picked.delete(String(id));
    }
    this.render();
  }

  renderVariants(step) {
    const slot = step.el.querySelector('[data-variant-slot]');
    const pills = slot.querySelector('[data-variant-pills]');
    pills.innerHTML = '';
    if (step.mode !== 'single' || step.picked.size !== 1) { slot.hidden = true; return; }
    const [pid, vid] = [...step.picked.entries()][0];
    const p = this.product(step, pid);
    if (!p || p.variants.length < 2) { slot.hidden = true; return; }
    slot.hidden = false;
    // Only show the options that actually differ between variants (e.g. Size, not "Size / Black")
    const varying = p.options.map((_, i) => new Set(p.variants.map((v) => (v.options || [])[i])).size > 1);
    const names = p.options.filter((_, i) => varying[i]);
    const label = (v) => {
      const parts = (v.options || []).filter((_, i) => varying[i]);
      return parts.length ? parts.join(' / ') : v.title;
    };
    slot.querySelector('[data-variant-label]').textContent = (names.length ? names : p.options).join(' / ');
    p.variants.forEach((v) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'dtn-pill';
      b.textContent = label(v);
      b.disabled = !v.available;
      b.setAttribute('aria-pressed', v.id === vid);
      if (!v.available) b.setAttribute('aria-label', v.title + ', sold out');
      b.addEventListener('click', () => { step.picked.set(String(p.id), v.id); this.render(); });
      pills.appendChild(b);
    });
  }

  items() {
    const out = [];
    this.steps.forEach((step) => {
      step.picked.forEach((vid, pid) => {
        const p = this.product(step, pid);
        const v = p && p.variants.find((x) => x.id === vid);
        if (p && v) {
          const varying = p.options.map((_, i) => new Set(p.variants.map((x) => (x.options || [])[i])).size > 1);
          const parts = (v.options || []).filter((_, i) => varying[i]);
          const suffix = p.variants.length > 1 ? ' / ' + (parts.length ? parts.join(' / ') : v.title) : '';
          out.push({ id: v.id, title: p.title + suffix, price: v.price });
        }
      });
    });
    return out;
  }

  render() {
    this.steps.forEach((s) => this.renderVariants(s));
    const items = this.items();
    const total = items.reduce((a, i) => a + i.price, 0);
    this.lines.innerHTML = '';
    items.forEach((i) => {
      const row = document.createElement('div');
      row.className = 'dtn-kit__line';
      const a = document.createElement('span'); a.textContent = i.title;
      const b = document.createElement('span'); b.textContent = this.fmt.format(i.price / 100);
      row.append(a, b);
      this.lines.appendChild(row);
    });
    this.totalEl.textContent = this.fmt.format(total / 100);
    if (this.threshold > 0) {
      const pct = Math.min(100, (total / this.threshold) * 100);
      this.bar.style.setProperty('--pct', pct + '%');
      this.bar.setAttribute('aria-valuenow', Math.round(Math.min(total, this.threshold) / 100));
      const left = this.threshold - total;
      this.ship.textContent = left > 0
        ? (this.dataset.msgLeft || '').replace('{amount}', this.fmt.format(left / 100))
        : (this.dataset.msgDone || '');
    } else {
      this.bar.hidden = true;
      this.ship.hidden = true;
    }
    this.addBtn.disabled = items.length === 0;
    this.error.textContent = '';
  }

  async add() {
    const items = this.items().map((i) => ({ id: i.id, quantity: 1 }));
    if (!items.length) { this.error.textContent = this.dataset.msgEmpty || ''; return; }
    this.addBtn.setAttribute('aria-busy', 'true');
    this.error.textContent = '';
    try {
      let Cart = null;
      try { ({ Cart } = await import('@troop-themes/main')); } catch (e) { Cart = null; }
      if (Cart && typeof Cart.addItems === 'function') {
        await Cart.addItems({
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items, sections: Cart.sections })
        }, this.addBtn);
        this.addBtn.textContent = this.dataset.msgAdded || this.addLabel;
        setTimeout(() => { this.addBtn.textContent = this.addLabel; }, 2500);
      } else {
        const res = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!res.ok) throw new Error(res.statusText);
        window.location.href = (window.Shopify?.routes?.cart || '/cart');
      }
    } catch (e) {
      this.error.textContent = 'Something went wrong adding your kit. One item may have just sold out. Please try again.';
    } finally {
      this.addBtn.removeAttribute('aria-busy');
    }
  }
}
if (!customElements.get('dtn-kit')) customElements.define('dtn-kit', DtnKit);

/* Quick add drawer: always open at the top so the product photo is in view */
(() => {
  const vp = document.querySelector('.layout--viewport');
  if (!vp) return;
  const toTop = () => document.querySelectorAll('.drawer--container[data-view="product-form"] .dtn-qa').forEach((f) => { f.scrollTop = 0; });
  new MutationObserver(() => {
    if (vp.getAttribute('data-drawer-view') === 'product-form') { toTop(); requestAnimationFrame(toTop); }
  }).observe(vp, { attributes: true, attributeFilter: ['data-drawer-view'] });
})();
